/**
 * A wrapper to handle the Modern pagination style, and infinite scroll.
 *
 * Usage:
 *  {%- if settings.pagination_style == "modern" or settings.pagination_infinite -%}
 *    <script src="{{ 'custom-pagination.js' | asset_url }}" defer="defer"></script>
 *  {%- endif -%}
 *
 *  {%- paginate thing by stuff -%}
 *    <custom-pagination data-infinite-scroll="{{ settings.pagination_infinite }}"
 *      data-pause-infinite-scroll="false">
 *      <ul>
 *        <li class="js-pagination-result">One</li>
 *        <li class="js-pagination-result">Two</li>
 *        <li class="js-pagination-result">Three</li>
 *      </ul>
 *      {%- if paginate.pages > 1 -%}
 *        {% render 'pagination', paginate: paginate, style: settings.pagination_style %}
 *      {%- endif -%}
 *    </custom-pagination>
 *  {%- endpaginate -%}
 */
if (!customElements.get('custom-pagination')) {
  class CustomPagination extends HTMLElement {
    constructor() {
      super();

      // persistent state (important for dedupe)
      this.seenKeys = new Set();
      this.isFetching = false;
      this.fetchingHref = null;

      this.init();
      document.addEventListener('on:bfcache:load-restore', this.reload.bind(this));
    }

    disconnectedCallback() {
      this.destroy();
    }

    init() {
      this.infiniteScroll = this.dataset.infiniteScroll === 'true';
      this.pagination = this.querySelector('.js-pagination');
      this.loadMoreButton = this.querySelector('.js-pagination-load-more');
      this.results = this.querySelectorAll('.js-pagination-result');
      this.controller = null;

      // seed dedupe set from existing items on the page
      this.seedSeenKeys();

      this.resultClickHandler = this.resultClickHandler || this.handleResultClick.bind(this);
      this.addEventListener('click', this.resultClickHandler);

      this.loadMoreClickHandler = this.loadMoreClickHandler || this.handleLoadMoreClick.bind(this);

      if (this.pagination && this.pagination.dataset.isMoreResults && this.results) {
        if (this.loadMoreButton) this.loadMoreButton.addEventListener('click', this.loadMoreClickHandler);

        if (this.infiniteScroll) {
          if (this.paginationObserver) this.paginationObserver.disconnect();

          this.paginationObserver = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (!entry.isIntersecting) return;

                // prevent rapid re-triggers while loading
                if (this.paginationObserver && this.pagination) {
                  this.paginationObserver.unobserve(this.pagination);
                }

                this.loadMore().finally(() => {
                  // rebind pagination + re-observe (pagination HTML changes)
                  if (this.pagination && this.paginationObserver && this.pagination.isConnected) {
                    this.paginationObserver.observe(this.pagination);
                  }
                });
              });
            },
            { rootMargin: `${window.innerHeight * 1.5}px 0px` }
          );

          this.paginationObserver.observe(this.pagination);
        }
      }
    }

    destroy() {
      if (this.paginationObserver) this.paginationObserver.disconnect();
      if (this.loadMoreButton) this.loadMoreButton.removeEventListener('click', this.loadMoreClickHandler);
      this.removeEventListener('click', this.resultClickHandler);

      this.loadMoreClickHandler = null;
      this.controller = null;
      this.fetchingHref = null;
      this.isFetching = false;
    }

    reload() {
      this.destroy();
      this.init();
    }

    handleLoadMoreClick(evt) {
      evt.preventDefault();
      this.loadMore();
    }

    handleResultClick(evt) {
      if (evt.target.closest('a') && evt.target.closest('.js-pagination-result') && this.controller) {
        this.controller.abort();
      }
    }

    // ---- DEDUPE HELPERS ----

    seedSeenKeys() {
      this.querySelectorAll('.js-pagination-result').forEach((el) => {
        const key = this.getResultKey(el);
        if (key) this.seenKeys.add(key);
      });
    }

    // Try multiple ways to identify the product uniquely
    getResultKey(resultEl) {
      // 1) direct dataset/attributes (best if present)
      const direct =
        resultEl.dataset.productId ||
        resultEl.getAttribute('data-product-id') ||
        resultEl.dataset.handle ||
        resultEl.getAttribute('data-handle') ||
        resultEl.dataset.productHandle ||
        resultEl.getAttribute('data-product-handle');

      if (direct) return String(direct);

      // 2) product link (common on Shopify product cards)
      const a = resultEl.querySelector('a[href*="/products/"]');
      const href = a?.getAttribute('href');
      if (!href) return null;

      try {
        const url = new URL(href, window.location.origin);
        // normalize: ignore query strings like ?variant= etc.
        return url.pathname;
      } catch {
        return href.split('?')[0];
      }
    }

    // ---- FETCH / APPEND ----

    async loadMore() {
      if (this.isFetching || this.dataset.pauseInfiniteScroll === 'true') return;

      const href = this.loadMoreButton?.href;
      if (!href) return;

      // if observer triggers twice quickly for same link, ignore the second
      if (this.fetchingHref === href) return;

      this.isFetching = true;
      this.fetchingHref = href;
      this.controller = new AbortController();

      try {
        this.loadMoreButton.classList.add('is-loading');
        this.loadMoreButton.setAttribute('aria-disabled', 'true');

        if (this.pagination?.dataset?.paginationStyle === 'traditional') {
          this.pagination.classList.add('is-loading');
        }

        const response = await fetch(href, { signal: this.controller.signal });
        if (!response.ok) throw new Error(response.status);

        const tmpl = document.createElement('template');
        tmpl.innerHTML = await response.text();

        // Build only NEW (non-duplicate) HTML
        let newResultsHtml = '';
        tmpl.content.querySelectorAll('.js-pagination-result').forEach((result) => {
          const key = this.getResultKey(result);
          if (!key) {
            // if we can't detect a key, allow it (better than breaking)
            newResultsHtml += result.outerHTML;
            return;
          }
          if (this.seenKeys.has(key)) return;
          this.seenKeys.add(key);
          newResultsHtml += result.outerHTML;
        });

        // Append new results (if any)
        if (newResultsHtml) {
          const lastResult = this.results[this.results.length - 1];
          if (lastResult) lastResult.insertAdjacentHTML('afterend', newResultsHtml);
          this.results = this.querySelectorAll('.js-pagination-result');
        }

        // Update pagination controls to next page (ALWAYS do this)
        const newPagination = tmpl.content.querySelector('.js-pagination');
        if (newPagination?.dataset?.isMoreResults) {
          this.pagination.innerHTML = newPagination.innerHTML;

          // rebind load more button
          if (this.loadMoreButton) {
            this.loadMoreButton.removeEventListener('click', this.loadMoreClickHandler);
          }
          this.loadMoreButton = this.querySelector('.js-pagination-load-more');
          if (this.loadMoreButton) {
            this.loadMoreButton.addEventListener('click', this.loadMoreClickHandler);
          }
        } else {
          // no more pages
          this.pagination?.remove();
          this.paginationObserver?.disconnect();
          document.querySelectorAll('.js-when-paginated-only').forEach((el) => el.remove());
        }
      } catch (error) {
        console.log('Unable to fetch next page of results', error);
      } finally {
        this.controller = null;
        this.fetchingHref = null;
        this.isFetching = false;

        if (this.loadMoreButton) {
          this.loadMoreButton.classList.remove('is-loading');
          this.loadMoreButton.removeAttribute('aria-disabled');
        }
        if (this.pagination?.dataset?.paginationStyle === 'traditional') {
          this.pagination.classList.remove('is-loading');
        }
      }
    }
  }

  customElements.define('custom-pagination', CustomPagination);
}
